import { useContext, useEffect, useId, useRef } from "react";

import { OVERLAY_Z_INDEX_BASE, OVERLAY_Z_INDEX_STEP } from "./constants";
import { OverlayContext, type OverlayRegisterOptions } from "./OverlayProvider";

export interface UseOverlayReturn {
  zIndex: number;
  isTopmost: boolean;
}

export const useOverlay = (
  onDismiss: () => void,
  enabled: boolean,
  options?: OverlayRegisterOptions,
): UseOverlayReturn => {
  const context = useContext(OverlayContext);
  const id = useId();
  const layer = options?.layer;
  const dismissOnOverlayClick = options?.dismissOnOverlayClick;

  // Store context functions in refs to avoid dependency issues
  const registerRef = useRef(context?.register);
  const unregisterRef = useRef(context?.unregister);
  registerRef.current = context?.register;
  unregisterRef.current = context?.unregister;

  useEffect(() => {
    const register = registerRef.current;
    const unregister = unregisterRef.current;

    if (!register || !unregister) {
      return;
    }

    if (enabled) {
      register(id, onDismiss, { layer, dismissOnOverlayClick });
    } else {
      unregister(id);
    }

    return () => {
      unregister(id);
    };
  }, [id, onDismiss, enabled, layer, dismissOnOverlayClick]);

  const overlays = context?.overlays ?? [];

  // Find this overlay in the stack
  const entry = overlays.find((e) => e.id === id);

  if (entry) {
    // Registered - use assigned z-index
    const maxZIndex = Math.max(...overlays.map((o) => o.zIndex));
    return {
      zIndex: entry.zIndex,
      isTopmost: entry.zIndex === maxZIndex,
    };
  }

  // Not registered yet - compute a z-index that will be higher than all existing overlays
  // This ensures the overlay appears on top during the first render before useEffect runs
  const maxExistingZIndex =
    overlays.length > 0
      ? Math.max(...overlays.map((o) => o.zIndex))
      : OVERLAY_Z_INDEX_BASE - OVERLAY_Z_INDEX_STEP;

  return {
    zIndex: maxExistingZIndex + OVERLAY_Z_INDEX_STEP,
    isTopmost: true,
  };
};

export default useOverlay;
